import { useMemo, useState, type FormEvent } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { ApiError, makeClient } from '../api/client'

const MIN_LEN = 8

export default function ResetPassword() {
  const [params] = useSearchParams()
  const token = useMemo(() => (params.get('token') ?? '').trim(), [params])
  const navigate = useNavigate()

  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const client = makeClient(() => null)

  const validationError = (() => {
    if (!token) return 'This link is missing its reset token — request a fresh one.'
    if (newPassword.length > 0 && newPassword.length < MIN_LEN) return `Password must be at least ${MIN_LEN} characters.`
    if (confirm.length > 0 && confirm !== newPassword) return 'Passwords do not match.'
    return null
  })()

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (validationError) {
      setError(validationError)
      return
    }
    setLoading(true)
    try {
      await client.call('/api/v1/auth/password-reset/confirm', {
        method: 'POST',
        body: { token, new_password: newPassword },
        authed: false,
      })
      setDone(true)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Reset failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="xs">
        <Stack spacing={2}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
              QEA FieldLens
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              an Insight Delivered product
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Set a new password
            </Typography>

            {done ? (
              <Stack spacing={2}>
                <Alert severity="success" variant="outlined">
                  Your password has been updated. Sign in with the new password.
                </Alert>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => navigate('/login', { replace: true })}
                >
                  Continue to sign in
                </Button>
              </Stack>
            ) : (
              <form onSubmit={onSubmit} noValidate>
                <Stack spacing={2}>
                  <TextField
                    label="New password"
                    type="password"
                    autoComplete="new-password"
                    autoFocus
                    required
                    fullWidth
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={loading || !token}
                    helperText={`At least ${MIN_LEN} characters.`}
                  />
                  <TextField
                    label="Confirm new password"
                    type="password"
                    autoComplete="new-password"
                    required
                    fullWidth
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    disabled={loading || !token}
                  />

                  {(error || (validationError && (newPassword || confirm))) && (
                    <Alert severity="error" variant="outlined">
                      {error ?? validationError}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    size="large"
                    disabled={loading || !token || !newPassword || !confirm || !!validationError}
                  >
                    {loading ? 'Updating…' : 'Update password'}
                  </Button>

                  <Link component={RouterLink} to="/login" variant="body2" sx={{ textAlign: 'center' }}>
                    Back to sign in
                  </Link>
                </Stack>
              </form>
            )}
          </Paper>
        </Stack>
      </Container>
    </Box>
  )
}
